import React, { ChangeEventHandler, MouseEventHandler, ReactElement, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';

import { RadioOrSelectOption } from '../../../../../types/common.types';
import { HashTag, Member, MemberProfile, Post } from '../../../../../types/entity.types';

import TribePostContainer from '../../../../../containers/programme/tribe/tribe-post/TribePost.container';

import TribeInfo from '../info/TribeInfo';
import ProfilePicture from '../../../../common/profile-picture/ProfilePicture';

import TribeFeedWrapper from '../feed/wrapper/TribeFeedWrapper';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';

import './tribe-profile.scss';

type Props = {
  profile?: MemberProfile;
  profilePicture?: string;
  status: string;
  posts: Post[];
  members: Member[];
  searchType?: RadioOrSelectOption;
  onSearchTypeChange: (option: RadioOrSelectOption) => void;
  filterValue: string;
  onFilterValueChange: ChangeEventHandler<HTMLInputElement>;
  refreshPost: (postExternalReference: string) => void;
  hashtags?: HashTag[];
  setComposing: (value: boolean) => void;
  loadPosts: () => void;
  onSearchClick: MouseEventHandler<HTMLButtonElement>;
  pendingApprovals?: number;
};

const TribeProfile = ({
  profile,
  status,
  posts,
  members,
  searchType,
  onSearchTypeChange,
  filterValue,
  onFilterValueChange,
  refreshPost,
  hashtags,
  setComposing,
  loadPosts,
  pendingApprovals,
  onSearchClick
}: Props): ReactElement => {
  const navigate: NavigateFunction = useNavigate();

  const { memberExternalReference }: Readonly<Params> = useParams();

  const scrollRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

  useEffect((): void => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [memberExternalReference]);

  const renderSubheading = (): ReactElement => {
    if (!profile) {
      return <React.Fragment />;
    }

    const { teamName, admin }: MemberProfile = profile;

    if (teamName && admin) {
      return <p>{teamName} | Admin</p>;
    }

    if (teamName) {
      return <p>{teamName}</p>;
    }

    if (admin) {
      return <p>Admin</p>;
    }

    return <React.Fragment />;
  };

  return (
    <div className="tribe-profile">
      <div className="tribe-profile__header">
        <ArrowIcon
          className="tribe-profile__header__arrow-icon"
          onClick={(): void => navigate(-1)}
          data-testid="arrow-icon"
        />
        <h1>Profile</h1>
      </div>

      <div className="tribe-profile__user">
        <ProfilePicture src={profile?.profilePicture} />
        <div className="tribe-profile__user__info">
          <p>{profile?.username}</p>
          {renderSubheading()}
        </div>
      </div>

      <div className="tribe-profile__content">
        <TribeFeedWrapper
          status={status}
          onSearchClick={onSearchClick}
          searchType={searchType}
          onSearchTypeChange={onSearchTypeChange}
          filterValue={filterValue}
          onFilterValueChange={onFilterValueChange}
        >
          <div
            className="tribe-profile__posts"
            ref={scrollRef}
            id="tribe-profile-posts"
            data-testid="tribe-profile-posts"
          >
            {posts.length > 0 && (
              <InfiniteScroll
                dataLength={posts.length}
                next={loadPosts}
                scrollableTarget="tribe-profile-posts"
                loader={<React.Fragment />}
                hasMore
              >
                {posts.map(
                  (post: Post): ReactElement => (
                    <TribePostContainer
                      key={post.externalReference}
                      status={status}
                      post={post}
                      hashtags={hashtags}
                      refreshPost={refreshPost}
                      setComposing={setComposing}
                      searchType={searchType}
                    />
                  )
                )}
              </InfiniteScroll>
            )}
            {posts.length === 0 && (
              <div className="tribe-profile__posts--empty">
                <p>No Posts</p>
              </div>
            )}
          </div>
        </TribeFeedWrapper>
        <TribeInfo status={status} pendingApprovals={pendingApprovals} members={members} />
      </div>
    </div>
  );
};

export default TribeProfile;
